import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, ScanLine, Route, AlertTriangle } from "lucide-react";

const AGENTS = [
  { key: "estimator", icon: Sparkles, name: "Pricing Estimator", desc: "Calculates final quotes from base cost + markup + shipping using OpenAI gpt-4o-mini." },
  { key: "rx_clarity", icon: ScanLine, name: "Rx Clarity Check", desc: "Reviews uploaded prescriptions for legibility before payment is allowed." },
  { key: "router", icon: Route, name: "Routing Agent", desc: "Routes orders to the best fulfillment centre based on automation rules." },
  { key: "error_detector", icon: AlertTriangle, name: "Error Detector", desc: "Flags missing data, invalid prescriptions, and license issues into the admin Alerts queue." },
];

export default function AdminAgents() {
  const [flags, setFlags] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/ai-agents"); setFlags(data || {}); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (agent) => {
    setSaving(agent);
    try {
      const next = !flags[agent];
      await api.put("/admin/ai-agents", { agent, enabled: next });
      setFlags({ ...flags, [agent]: next });
      toast.success(`${agent} ${next ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(null); }
  };

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-5xl" data-testid="admin-agents">
      <div className="mb-10">
        <p className="font-manrope text-xs uppercase tracking-widest text-paw-text-muted dark:text-white/40 mb-2">AI Control</p>
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Agents</h1>
        <p className="font-manrope text-sm text-paw-text-muted dark:text-white/40 mt-2">Toggle automated agents on or off. Changes apply immediately to new requests.</p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-paw-text-muted dark:text-white/40 font-manrope text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="space-y-3">
          {AGENTS.map(({ key, icon: Icon, name, desc }) => (
            <div key={key} className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5 flex items-center justify-between gap-6" data-testid={`agent-${key}`}>
              <div className="flex items-start gap-4 flex-1">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${flags[key] ? "bg-paw-accent/15" : "bg-black/[0.03] dark:bg-white/5"}`}>
                  <Icon className={`w-4 h-4 ${flags[key] ? "text-paw-accent" : "text-paw-text-muted dark:text-white/30"}`} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-outfit font-semibold text-base">{name}</p>
                  <p className="font-manrope text-sm text-paw-text-muted dark:text-white/50 mt-1">{desc}</p>
                </div>
              </div>
              <button
                onClick={() => toggle(key)}
                disabled={saving === key}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${flags[key] ? "bg-paw-accent" : "bg-black/[0.06] dark:bg-white/10"} ${saving === key ? "opacity-50" : ""}`}
                data-testid={`agent-toggle-${key}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${flags[key] ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
